import React, { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { formatInStoreTimezone } from '../../utils/dateUtils';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';
import { calculateCashReconciliation } from '../../utils/cashRounding';

const ZReportModal = ({ session, onClose, timezone = 'Australia/Melbourne' }) => {
  const printRef = useRef(null);

  useEffect(() => {
    if (!session) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [session]);

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatCurrencyValue = (value) => `$${Number(value || 0).toFixed(2)}`;

  const buildBrowserPrintableHtml = () => {
    if (!printRef.current) {
      return null;
    }
    const content = printRef.current.innerHTML;
    return `
      <!doctype html>
      <html>
        <head>
          <title>Z-Report</title>
          <style>
            body { font-family: sans-serif; padding: 24px; color: #0f172a; }
            h2, h3 { margin-top: 24px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #cbd5f5; padding: 8px; font-size: 14px; }
            th { background: #f8fafc; text-align: left; }
            .meta p { margin: 4px 0; }
            section { margin-top: 20px; }
            .kpis { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 12px; }
            .kpi { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
            .kpi strong { display: block; margin-top: 4px; font-size: 16px; }
            .muted { color: #64748b; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `;
  };

  const buildEscPosPrintableHtml = () => {
    if (!session) return null;

    const salesSummary = session.salesSummary || {};
    const refundsSummary = session.refundsSummary || {};
    const reconciliation = calculateCashReconciliation({
      openingFloat: session.openingFloat,
      cashSales: salesSummary.cash?.total || 0,
      cashRefunds: refundsSummary.cash?.total || 0,
      countedCash: session.closingFloat,
      leaveAmount: session.closingLeaveAmount,
    });
    const salesTotal = Object.values(salesSummary).reduce((acc, entry) => acc + (entry?.total || 0), 0);
    const refundsTotal = Object.values(refundsSummary).reduce((acc, entry) => acc + (entry?.total || 0), 0);
    const salesCount = Object.values(salesSummary).reduce((acc, entry) => acc + (entry?.count || 0), 0);
    const refundsCount = Object.values(refundsSummary).reduce((acc, entry) => acc + (entry?.count || 0), 0);
    const netTotal = salesTotal - refundsTotal;

    const formatMethod = (method) => method ? method.charAt(0).toUpperCase() + method.slice(1) : 'Unknown';
    const methodKeys = Array.from(new Set([...Object.keys(salesSummary), ...Object.keys(refundsSummary)]));

    const buildDenominationRows = (denominations) => {
      if (!denominations || typeof denominations !== 'object') return [];
      return Object.entries(denominations)
        .map(([den, count]) => ({ den: Number(den), count: Number(count) || 0 }))
        .filter((entry) => Number.isFinite(entry.den) && entry.count > 0)
        .sort((a, b) => b.den - a.den);
    };

    const openingDenoms = buildDenominationRows(session.openingDenominations);
    const closingDenoms = buildDenominationRows(session.closingDenominations);
    const openedAt = session.openingTime ? new Date(session.openingTime) : null;
    const closedAt = session.closingTime ? new Date(session.closingTime) : null;
    const durationMinutes = openedAt && closedAt ? Math.max(0, Math.round((closedAt - openedAt) / 60000)) : null;
    const durationLabel = durationMinutes !== null
      ? `${Math.floor(durationMinutes / 60)}h ${durationMinutes % 60}m`
      : '—';

    const tenderRows = methodKeys.map((method) => {
      const sales = salesSummary[method]?.total || 0;
      const refunds = refundsSummary[method]?.total || 0;
      return `
        <tr>
          <td>${escapeHtml(formatMethod(method))}</td>
          <td>${escapeHtml(formatCurrencyValue(sales))}</td>
          <td>${escapeHtml(formatCurrencyValue(refunds))}</td>
          <td>${escapeHtml(formatCurrencyValue(sales - refunds))}</td>
        </tr>
      `;
    }).join('');

    const buildDenomTable = (rows) => rows.map((entry) => `
      <tr>
        <td>${escapeHtml(formatCurrencyValue(entry.den))} x ${escapeHtml(entry.count)}</td>
        <td>${escapeHtml(formatCurrencyValue(entry.den * entry.count))}</td>
      </tr>
    `).join('');

    const closingNoteBlock = (typeof session.closingNote === 'string' && session.closingNote.trim())
      ? `<p>Note: ${escapeHtml(session.closingNote)}</p>`
      : '';

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Z-Report</title>
          <style>
            body { margin: 0; padding: 10px; background: #fff; color: #111827; font-family: "Courier New", Courier, monospace; }
            #receipt-to-print { width: 288px; margin: 0 auto; font-size: 12px; line-height: 1.35; }
            h2, h3 { margin: 0 0 4px 0; }
            p { margin: 0 0 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 4px; }
            th, td { padding: 0; font-size: 12px; }
            .section { margin-top: 8px; }
          </style>
        </head>
        <body>
          <div id="receipt-to-print">
            <h2>Z-REPORT</h2>
            <p>Generated ${escapeHtml(formatInStoreTimezone(new Date(), 'dd/MM/yyyy HH:mm', timezone))}</p>
            <p>Store: ${escapeHtml(session?.store?.name || '')}</p>
            <p>Till: ${escapeHtml(session?.till?.name || '')}</p>
            <p>Opened: ${escapeHtml(formatInStoreTimezone(session.openingTime, 'dd/MM/yyyy HH:mm', timezone))}</p>
            <p>Closed: ${escapeHtml(session.closingTime ? formatInStoreTimezone(session.closingTime, 'dd/MM/yyyy HH:mm', timezone) : '—')}</p>
            <p>Duration: ${escapeHtml(durationLabel)}</p>
            ${closingNoteBlock}

            <div class="section">
              <h3>Totals</h3>
              <p>Sales (${escapeHtml(salesCount)}): ${escapeHtml(formatCurrencyValue(salesTotal))}</p>
              <p>Refunds (${escapeHtml(refundsCount)}): ${escapeHtml(formatCurrencyValue(refundsTotal))}</p>
              <p>Net: ${escapeHtml(formatCurrencyValue(netTotal))}</p>
              <p>Cash Variance: ${escapeHtml(formatCurrencyValue(reconciliation.cashVarianceAmount))}</p>
            </div>

            <div class="section">
              <h3>Cash Reconciliation</h3>
              <table>
                <tbody>
                  <tr><td>Opening Float</td><td>${escapeHtml(formatCurrencyValue(reconciliation.openingFloatAmount))}</td></tr>
                  <tr><td>Cash Sales</td><td>${escapeHtml(formatCurrencyValue(salesSummary.cash?.total || 0))}</td></tr>
                  <tr><td>Cash Refunds</td><td>${escapeHtml(formatCurrencyValue(refundsSummary.cash?.total || 0))}</td></tr>
                  <tr><td>Expected Cash</td><td>${escapeHtml(formatCurrencyValue(reconciliation.expectedCashAmount))}</td></tr>
                  <tr><td>Counted Cash</td><td>${escapeHtml(formatCurrencyValue(reconciliation.countedCashAmount))}</td></tr>
                  <tr><td>Target Float</td><td>${escapeHtml(formatCurrencyValue(reconciliation.targetFloatAmount))}</td></tr>
                  <tr><td>Bank Deposit</td><td>${escapeHtml(formatCurrencyValue(reconciliation.bankDepositAmount))}</td></tr>
                  <tr><td>Float Left in Till</td><td>${escapeHtml(formatCurrencyValue(reconciliation.actualFloatAmount))}</td></tr>
                  <tr><td>Variance</td><td>${escapeHtml(formatCurrencyValue(reconciliation.cashVarianceAmount))}</td></tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h3>Tender Mix</h3>
              <table>
                <thead>
                  <tr><th>Method</th><th>Sales</th><th>Refunds</th><th>Net</th></tr>
                </thead>
                <tbody>
                  ${tenderRows}
                </tbody>
              </table>
            </div>

            ${closingDenoms.length > 0 ? `
              <div class="section">
                <h3>Closing Denominations</h3>
                <table>
                  <tbody>
                    ${buildDenomTable(closingDenoms)}
                  </tbody>
                </table>
              </div>
            ` : ''}

            ${openingDenoms.length > 0 ? `
              <div class="section">
                <h3>Opening Denominations</h3>
                <table>
                  <tbody>
                    ${buildDenomTable(openingDenoms)}
                  </tbody>
                </table>
              </div>
            ` : ''}
          </div>
        </body>
      </html>
    `;
  };

  const getPrinterPlugin = () => {
    if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
      return null;
    }
    return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
  };

  const tryNativePrint = async (html) => {
    const plugin = getPrinterPlugin();
    if (!plugin) {
      return false;
    }
    try {
      const payload = { html, jobType: 'zreport' };
      const printerConfig = getPreferredPrinterConfig('receipt');
      if (printerConfig.transport === 'network' && printerConfig.host) {
        payload.networkHost = printerConfig.host;
        if (printerConfig.port) {
          payload.networkPort = printerConfig.port;
        }
      } else if (printerConfig.bluetoothName) {
        payload.bluetoothNameHint = printerConfig.bluetoothName;
      }
      await plugin.print(payload);
      return true;
    } catch (err) {
      console.warn('[ZReportModal] Native print failed', err);
      return false;
    }
  };

  const handlePrint = async () => {
    const nativePrintableHtml = buildEscPosPrintableHtml();
    if (!nativePrintableHtml) return;

    if (await tryNativePrint(nativePrintableHtml)) {
      return;
    }

    const printableHtml = buildBrowserPrintableHtml();
    if (!printableHtml) return;

    const printWindow = window.open('', '', 'width=720,height=900');
    if (!printWindow) return;
    printWindow.document.write(printableHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (!session) return null;

  const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;
  const salesSummary = session.salesSummary || {};
  const refundsSummary = session.refundsSummary || {};
  const reconciliation = calculateCashReconciliation({
    openingFloat: session.openingFloat,
    cashSales: salesSummary.cash?.total || 0,
    cashRefunds: refundsSummary.cash?.total || 0,
    countedCash: session.closingFloat,
    leaveAmount: session.closingLeaveAmount,
  });
  const salesTotal = Object.values(salesSummary).reduce((acc, entry) => acc + (entry?.total || 0), 0);
  const refundsTotal = Object.values(refundsSummary).reduce((acc, entry) => acc + (entry?.total || 0), 0);
  const salesCount = Object.values(salesSummary).reduce((acc, entry) => acc + (entry?.count || 0), 0);
  const refundsCount = Object.values(refundsSummary).reduce((acc, entry) => acc + (entry?.count || 0), 0);
  const netTotal = salesTotal - refundsTotal;

  const formatMethod = (method) => method ? method.charAt(0).toUpperCase() + method.slice(1) : 'Unknown';
  const methodKeys = Array.from(
    new Set([...Object.keys(salesSummary), ...Object.keys(refundsSummary)])
  );

  const buildDenominationRows = (denominations) => {
    if (!denominations || typeof denominations !== 'object') return [];
    return Object.entries(denominations)
      .map(([den, count]) => ({ den: Number(den), count: Number(count) || 0 }))
      .filter((entry) => Number.isFinite(entry.den) && entry.count > 0)
      .sort((a, b) => b.den - a.den);
  };

  const openingDenoms = buildDenominationRows(session.openingDenominations);
  const closingDenoms = buildDenominationRows(session.closingDenominations);

  const openedAt = session.openingTime ? new Date(session.openingTime) : null;
  const closedAt = session.closingTime ? new Date(session.closingTime) : null;
  const durationMinutes = openedAt && closedAt ? Math.max(0, Math.round((closedAt - openedAt) / 60000)) : null;
  const durationLabel = durationMinutes !== null
    ? `${Math.floor(durationMinutes / 60)}h ${durationMinutes % 60}m`
    : '—';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div ref={printRef}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-2xl font-bold text-slate-900">Z-Report</h2>
            <div className="text-xs text-slate-500">
              Generated {formatInStoreTimezone(new Date(), 'dd-MM-yyyy HH:mm', timezone)}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm meta">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Store</p>
                <p className="font-semibold text-slate-900">{session.store.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Till</p>
                <p className="font-semibold text-slate-900">{session.till.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Opened</p>
                <p>{formatInStoreTimezone(session.openingTime, 'dd-MM-yyyy HH:mm', timezone)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Closed</p>
                <p>
                  {session.closingTime
                    ? formatInStoreTimezone(session.closingTime, 'dd-MM-yyyy HH:mm', timezone)
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Session Duration</p>
                <p>{durationLabel}</p>
              </div>
            </div>
          </div>

          <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-lg font-bold mb-3 text-slate-900">Totals</h3>
            <div className="kpis grid gap-3 sm:grid-cols-2">
              <div className="kpi rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="muted text-xs uppercase tracking-wide">Sales ({salesCount})</span>
                <strong className="text-lg text-slate-900">{formatCurrency(salesTotal)}</strong>
              </div>
              <div className="kpi rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="muted text-xs uppercase tracking-wide">Refunds ({refundsCount})</span>
                <strong className="text-lg text-slate-900">{formatCurrency(refundsTotal)}</strong>
              </div>
              <div className="kpi rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="muted text-xs uppercase tracking-wide">Net</span>
                <strong className="text-lg text-slate-900">{formatCurrency(netTotal)}</strong>
              </div>
              <div className="kpi rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <span className="muted text-xs uppercase tracking-wide">Cash Variance</span>
                <strong className="text-lg text-slate-900">{formatCurrency(reconciliation.cashVarianceAmount)}</strong>
              </div>
            </div>
          </section>

          <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-lg font-bold mb-3 text-slate-900">Cash Reconciliation</h3>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <tbody>
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 text-slate-600">Opening Float</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatCurrency(reconciliation.openingFloatAmount)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-600">Cash Sales</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(salesSummary.cash?.total || 0)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-600">Cash Refunds</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(refundsSummary.cash?.total || 0)}</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 text-slate-600">Expected Cash</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatCurrency(reconciliation.expectedCashAmount)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-600">Counted Cash</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(reconciliation.countedCashAmount)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-600">Target Float</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(reconciliation.targetFloatAmount)}</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 text-slate-600">Bank Deposit</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatCurrency(reconciliation.bankDepositAmount)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-slate-600">Float Left in Till</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(reconciliation.actualFloatAmount)}</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 text-slate-600">Variance</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatCurrency(reconciliation.cashVarianceAmount)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {typeof session.closingNote === 'string' && session.closingNote.trim() ? (
            <section className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
              <h3 className="text-lg font-bold mb-2 text-amber-900">Handover Note</h3>
              <p className="text-sm text-amber-900">{session.closingNote}</p>
            </section>
          ) : null}

          <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-lg font-bold mb-3 text-slate-900">Tender Mix</h3>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Payment Method</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right">Refunds</th>
                  <th className="px-3 py-2 text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {methodKeys.map((method) => {
                  const sales = salesSummary[method]?.total || 0;
                  const refunds = refundsSummary[method]?.total || 0;
                  return (
                    <tr key={method}>
                      <td className="px-3 py-2">{formatMethod(method)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(sales)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(refunds)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatCurrency(sales - refunds)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {closingDenoms.length > 0 && (
            <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-lg font-bold mb-3 text-slate-900">Closing Denominations</h3>
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Denomination</th>
                    <th className="px-3 py-2 text-right">Count</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {closingDenoms.map((entry) => (
                    <tr key={`close-${entry.den}`}>
                      <td className="px-3 py-2">{formatCurrency(entry.den)}</td>
                      <td className="px-3 py-2 text-right">{entry.count}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(entry.den * entry.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {openingDenoms.length > 0 && (
            <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-lg font-bold mb-3 text-slate-900">Opening Denominations</h3>
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Denomination</th>
                    <th className="px-3 py-2 text-right">Count</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {openingDenoms.map((entry) => (
                    <tr key={`open-${entry.den}`}>
                      <td className="px-3 py-2">{formatCurrency(entry.den)}</td>
                      <td className="px-3 py-2 text-right">{entry.count}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(entry.den * entry.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>

        <div className="flex justify-end pt-4 gap-3">
          <button onClick={handlePrint} className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800">
            Print
          </button>
          <button onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ZReportModal;
